import * as React from 'react';
import styled, { css } from 'styled-components';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback2';

type Props = {
    alt: string;
    className?: string;
    placeholder: string;
    srcPrimary: string;
    srcSecondary: string;
};

const ImageWithHover = (props: Props) => {
    const { alt = '', className = '', placeholder = '', srcPrimary = '', srcSecondary = '' } = props;

    const [hover, setHover] = React.useState(false);
    const [primaryError, setPrimaryError] = React.useState(false);
    const [secondaryError, setSecondaryError] = React.useState(false);

    const handleMouseEnter = React.useCallback(() => {
        setHover(true);
    }, []);

    const handleMouseLeave = React.useCallback(() => {
        setHover(false);
    }, []);

    const handlePrimaryError = React.useCallback(() => {
        setPrimaryError(true);
    }, []);

    const handleSecondaryError = React.useCallback(() => {
        setSecondaryError(true);
    }, []);

    let source = srcPrimary ? srcPrimary : srcSecondary;
    let showSingleImage = false;

    if (primaryError) {
        if (!secondaryError) {
            showSingleImage = true;
            source = srcSecondary;
        }
    }

    if (secondaryError) {
        if (!primaryError) {
            showSingleImage = true;
            source = srcPrimary;
        }
    }

    if (showSingleImage) {
        return (
            <div className={className}>
                <DefaultImageWithFallback alt={alt} placeholder={placeholder} src={source} />
            </div>
        );
    }

    return (
        <div className={className} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <SecondaryImageContainer hover={hover}>
                {hover && (
                    <DefaultImageWithFallback
                        alt={alt}
                        lazy={false}
                        onError={handleSecondaryError}
                        placeholder={false}
                        src={srcSecondary}
                    />
                )}
            </SecondaryImageContainer>
            <PrimaryImageWithFallback
                $usePrimaryStyle={Boolean(srcSecondary) && !secondaryError}
                alt={alt}
                onError={handlePrimaryError}
                placeholder={placeholder}
                src={source}
            />
        </div>
    );
};

export default React.memo<Props>(ImageWithHover);

const DefaultImageWithFallback = styled(ImageWithFallback)`
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    margin: auto;
    display: block;
    max-height: 100%;
    max-width: 100%;
    @media print {
        margin: 0;
    }
`;

interface PrimaryProps {
    $usePrimaryStyle: boolean;
}

const PrimaryImageWithFallback = styled(ImageWithFallback)<PrimaryProps>`
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    margin: auto;
    display: block;
    max-height: 100%;
    max-width: 100%;
    @media print {
        margin: 0;
    }

    &:hover {
        opacity: ${({ $usePrimaryStyle }) => ($usePrimaryStyle ? 0 : 1)};
        transition: ${({ $usePrimaryStyle }) => ($usePrimaryStyle ? `opacity 0.8s ease-in-out` : `none`)};
        transition-delay: ${({ $usePrimaryStyle }) => ($usePrimaryStyle ? `0.3s` : `0s`)};
    }
`;

const onHover = css`
    opacity: 1;
    transition: opacity 0.6s ease-in-out;
    transition-delay: 0.3s;
`;

const SecondaryImageContainer = styled.div<{ hover }>`
    opacity: 0;
    ${(props) => props.hover && onHover}
`;
